import React from 'react';
import { InfoIcon, CheckCircleIcon, ArrowUpCircleIcon, LayersIcon } from 'lucide-react';
import { oohData } from '../utils/oohData';
const getCategoryBorder = (category: string) => {
  if (category === 'Visual Capture') return 'border-l-blue-400';
  if (category === 'Shape & Edge') return 'border-l-purple-400';
  if (category === 'Biological Attention') return 'border-l-pink-400';
  if (category === 'Structural & Cognitive') return 'border-l-orange-400';
  return 'border-l-gray-300';
};
type Driver = (typeof oohData.drivers)[number];
const DriverTitle: React.FC<{
  name: string;
  definition?: string;
}> = ({
  name,
  definition
}) => <div className="flex items-center space-x-2" data-id="element-162" data-mp="2hb2cpj6d9jnx">
    <h4 className="font-bold text-gray-900 text-sm tracking-wide" data-id="element-163" data-mp="2cbpksk0n1meh">{name}</h4>
    {definition && <span className="relative group inline-flex" data-export-hide data-id="element-164" data-mp="289ktvt1kr3vj">
        <button type="button" aria-label={`What ${name} measures`} className="text-gray-300 hover:text-[#1E90FF] focus:text-[#1E90FF] focus:outline-none transition-colors" data-id="element-165" data-mp="28bnkae6n2z0s">
          <InfoIcon className="h-3.5 w-3.5" data-id="element-166" data-mp="1hsyt010ahj11" />
        </button>
        <span role="tooltip" className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-60 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-xs font-normal leading-relaxed text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100" data-id="element-167" data-mp="1hujl7jz4spvq">
          {definition}
        </span>
      </span>}
  </div>;
export const DriverAnalysis: React.FC = () => {
  const {
    drivers,
    driverDefinitions,
    methodology
  } = oohData;
  const notApplicable = drivers.filter(d => d.verdict === 'neutral_not_applicable');
  const improvable = drivers.filter(d => d.verdict !== 'neutral_not_applicable' && d.improvement);
  const passed = drivers.filter(d => d.verdict !== 'neutral_not_applicable' && !d.improvement);
  return <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-168" data-mp="2i15iprtg54g7">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3" data-id="element-169" data-mp="2i0nea7wmrjx7">
        <div className="flex items-center space-x-3" data-id="element-170" data-mp="2d33p4cye9mqh">
          <div className="p-2 bg-[#1E90FF]/10 text-[#1E90FF] rounded-lg" data-id="element-171" data-mp="2d4eu2g3kt0j6">
            <LayersIcon className="w-5 h-5" data-id="element-172" data-mp="289j0vcmls74h" />
          </div>
          <h2 className="text-lg font-medium text-gray-800" data-id="element-173" data-mp="24ajwbt4sacdk">
            Attention Driver Analysis
          </h2>
        </div>
        <div className="flex items-center gap-4 text-xs text-gray-500" data-id="element-174" data-mp="1zc8otp1yodyi">
          <span data-id="element-175" data-mp="1ubb5381azbuw">{methodology.driverCount} drivers assessed</span>
          <span className="inline-flex items-center gap-1.5" data-id="element-176" data-mp="1pcht5k1nzswu">
            <span className="export-dot-down w-2 h-2 rounded-full bg-green-500" data-id="element-177" data-mp="1pczv8xb0d1co" />
            {passed.length} working well
          </span>
          <span className="inline-flex items-center gap-1.5" data-id="element-178" data-mp="3jqinpsq6ltr4">
            <span className="export-dot-down w-2 h-2 rounded-full bg-amber-500" data-id="element-179" data-mp="3jppsnp0l4sot" />
            {improvable.length} to improve
          </span>
        </div>
      </div>

      {/* Opportunities to improve — most important, shown first */}
      <section className="mb-8" data-id="element-180" data-mp="16g96qr2a7mtq">
        <div className="flex items-center space-x-2 mb-4" data-id="element-181" data-mp="16f7kdqftfdwp">
          <ArrowUpCircleIcon className="export-icon-down h-5 w-5 text-amber-600" data-id="element-182" data-mp="1b6xy86dyojsv" />
          <h3 className="text-sm font-semibold text-amber-800 uppercase tracking-wider" data-id="element-183" data-mp="1b92apcpxer4r">
            Opportunities to improve
          </h3>
        </div>
        <div className="space-y-4" data-id="element-184" data-mp="ijbwz4s7hr96">
          {improvable.map((driver: Driver) => <div key={driver.name} className={`border border-gray-100 rounded-xl p-4 border-l-4 ${getCategoryBorder(driver.category)} bg-white hover:shadow-md transition-shadow`} data-id="element-185" data-mp="nglhq74nheqo">
              <div className="flex flex-col md:flex-row md:items-start gap-4" data-id="element-186" data-mp="wiusmy3tza4l">
                <div className="md:w-1/4 flex flex-col space-y-1.5" data-id="element-187" data-mp="22yx019dsagtf">
                  <DriverTitle name={driver.name} definition={driverDefinitions[driver.name]} data-id="element-188" data-mp="27x89hj062pb4" />
                  <span className="text-xs text-gray-500 font-medium" data-id="element-189" data-mp="26gl36049gzh9">
                    {driver.category}
                  </span>
                </div>
                <div className="md:w-3/4 space-y-3" data-id="element-190" data-mp="2fghnjpjmbc6i">
                  <p className="text-sm text-gray-700 leading-relaxed" data-id="element-191" data-mp="2fi2gjaqaan29">
                    {driver.observation}
                  </p>
                  <div className="bg-amber-50 border border-amber-100 rounded-md p-3 flex items-start space-x-3" data-id="element-192" data-mp="2pexcblv6a9cu">
                    <div className="export-dot-down w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 flex-shrink-0" data-id="element-193" data-mp="2kgcbplc0dnnv" />
                    <p className="text-sm text-amber-900 leading-relaxed" data-id="element-194" data-mp="2z506vqhxzvda">
                      <span className="font-semibold mr-1" data-id="element-195" data-mp="2u5vwwxn71tlw">Fix:</span>
                      {driver.improvement}
                    </p>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </section>

      {/* Working well / passed */}
      <section className="mb-8" data-id="element-196" data-mp="3ms3ybajnylw5">
        <div className="flex items-center space-x-2 mb-4" data-id="element-197" data-mp="3mt5eftfjdykq">
          <CheckCircleIcon className="export-icon-down h-5 w-5 text-green-600" data-id="element-198" data-mp="3rr6t825xjbqe" />
          <h3 className="text-sm font-semibold text-green-800 uppercase tracking-wider" data-id="element-199" data-mp="3rp44rmkfdqns">
            Working well
          </h3>
        </div>
        <div className="space-y-4" data-id="element-200" data-mp="3hs9a5er9waes">
          {passed.map((driver: Driver) => <div key={driver.name} className={`border border-gray-100 rounded-xl p-4 border-l-4 ${getCategoryBorder(driver.category)} bg-white hover:shadow-md transition-shadow`} data-id="element-201" data-mp="341w1192napnv">
              <div className="flex flex-col md:flex-row md:items-start gap-4" data-id="element-202" data-mp="2u47wjrf8m3si">
                <div className="md:w-1/4 flex flex-col space-y-1.5" data-id="element-203" data-mp="2z29m8v7651cy">
                  <DriverTitle name={driver.name} definition={driverDefinitions[driver.name]} data-id="element-204" data-mp="2z0gd9993251s" />
                  <span className="text-xs text-gray-500 font-medium" data-id="element-205" data-mp="2q0tfcxfkbhdh">
                    {driver.category}
                  </span>
                </div>
                <div className="md:w-3/4" data-id="element-206" data-mp="quwfle4cyo8u">
                  <p className="text-sm text-gray-700 leading-relaxed" data-id="element-207" data-mp="qtv2l43s7roh">
                    {driver.observation}
                  </p>
                </div>
              </div>
            </div>)}
        </div>
      </section>

      {/* Not applicable — kept deliberately vague, no per-driver breakdown */}
      {notApplicable.length > 0 && <div className="bg-gray-50/50 border border-gray-100 rounded-xl p-4" data-id="element-208" data-mp="1ei4hdm2kvf64">
          <p className="text-sm text-gray-500 leading-relaxed" data-id="element-209" data-mp="19m76vwdzx319">
            <span className="font-medium text-gray-600" data-id="element-210" data-mp="15iran15u94p3">
              The remaining drivers from the Attention Blueprint
            </span>{' '}
            were assessed and are not applicable to this execution, largely
            biological attention cues that are not present in the creative.
          </p>
        </div>}
    </div>;
};